/**
 * SkillDelivery Platform
 * CardGrid — responsive card list for Research + Offerings sections.
 *
 * Layout:
 *   - 3 columns at ≥1100 px, 2 at ≥700 px, 1 on phone.
 *   - Cards in a row have equal heights (CSS Grid stretches by default).
 *   - "Learn more →" is pinned to the bottom of each card so a longer
 *     one-liner on one card never pushes its link below its neighbours'.
 *
 * Colour: the icon square keeps its kind-specific gradient so the eye
 * can scan sections visually. "Learn more" is SkillDelivery cyan (not
 * the kind gradient) — only the gradient banner, sidebar customer-block
 * accent, and current roadmap stage track the customer brand colour.
 */
import React, { useEffect, useState } from 'react';
import {
  BookOpen, Lightbulb, Briefcase, Cpu, Rocket, Sparkles, ArrowUpRight
} from 'lucide-react';

const ICON_BY_KIND = {
  insight:   Lightbulb,
  research:  BookOpen,
  services:  Briefcase,
  ai:        Cpu,
  grow:      Rocket,
  resources: Sparkles
};

const GRADIENT_BY_KIND = {
  insight:   ['#f97316', '#ea580c'],
  research:  ['#8b5cf6', '#7c3aed'],
  services:  ['#06b6d4', '#0891b2'],
  ai:        ['#3b82f6', '#1d4ed8'],
  grow:      ['#10b981', '#059669'],
  resources: ['#f59e0b', '#d97706']
};

// Only the darker shade is used (for "Learn more →"); the lighter cyan
// lives in InviteWelcome.jsx / BrandSidebar.jsx.
const SKD_CYAN_DARK = '#0e7490';

function _cols() {
  if (typeof window === 'undefined') return 3;
  const w = window.innerWidth;
  if (w >= 1100) return 3;
  if (w >= 700)  return 2;
  return 1;
}

/**
 * @param {object} props
 * @param {string} props.kind    One of ICON_BY_KIND keys.
 * @param {Array}  props.items   Each: {id, title|name, one_liner, link_url, source?, published_date?, status?}
 * @param {function(itemId:string):void} props.onClick  Click tracker (also opens the link).
 */
export default function CardGrid({ kind, items, onClick }) {
  const [cols, setCols] = useState(_cols());
  useEffect(() => {
    const onResize = () => setCols(_cols());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (!Array.isArray(items) || items.length === 0) return null;
  const Icon = ICON_BY_KIND[kind] || Sparkles;
  const [c1, c2] = GRADIENT_BY_KIND[kind] || GRADIENT_BY_KIND.resources;

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
      gap: 14,
      alignItems: 'stretch'
    }}>
      {items.map((it) => (
        <article
          key={it.id}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            padding: 16,
            borderRadius: 12,
            border: '1px solid #e2e8f0',
            background: 'white',
            boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
            height: '100%'
          }}
        >
          <div style={{
            width: 44,
            height: 44,
            borderRadius: 10,
            background: `linear-gradient(135deg, ${c1}, ${c2})`,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 2px 4px rgba(15,23,42,0.08)'
          }}>
            <Icon size={22} />
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'start',
            gap: 8
          }}>
            <h4 style={{ margin: 0, fontSize: 15, color: '#0f172a', lineHeight: 1.3 }}>
              {it.title || it.name}
            </h4>
            {it.status === 'planned' && (
              <span style={{
                fontSize: 10, padding: '2px 8px', borderRadius: 999,
                background: '#fef9c3', color: '#854d0e', whiteSpace: 'nowrap',
                fontWeight: 600
              }}>
                coming soon
              </span>
            )}
          </div>

          <p style={{ margin: 0, fontSize: 13, color: '#475569', lineHeight: 1.5 }}>
            {it.one_liner}
          </p>

          {(it.source || it.published_date) && (
            <p style={{ margin: 0, fontSize: 11, color: '#94a3b8' }}>
              {it.source || ''}
              {it.source && it.published_date ? ' · ' : ''}
              {it.published_date ? String(it.published_date).slice(0, 10) : ''}
            </p>
          )}

          {/* Pin Learn more to the bottom — the flex column + marginTop
              auto keeps it anchored regardless of the one-liner length,
              so cards in a row look aligned. */}
          <a
            href={it.link_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => { if (onClick) onClick(it.id); }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              marginTop: 'auto',
              color: SKD_CYAN_DARK,
              fontWeight: 600,
              fontSize: 13,
              textDecoration: 'none'
            }}
          >
            Learn more <ArrowUpRight size={14} />
          </a>
        </article>
      ))}
    </div>
  );
}
